import { ILCHttpResponse } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { RootState } from '../redux/store'
import {
  IGetNotificationsParams,
  INotification,
  IPaginatedNotifications,
  ISendNotificationParams,
  IUnreadCountByFolder,
} from '../ui/awareComponents/lc-notifications/lc-notifications.interface'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/app-notifications`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  tagTypes: ['Notifications'],
  endpoints: (builder) => ({
    getNotifications: builder.query<ILCHttpResponse<IPaginatedNotifications>, IGetNotificationsParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams()
        if (params?.locale) searchParams.set('locale', params.locale)
        if (params?.folder) searchParams.set('folder', params.folder)
        if (params?.page) searchParams.set('page', String(params.page))
        if (params?.limit) searchParams.set('limit', String(params.limit))
        const qs = searchParams.toString()
        return { url: qs ? `?${qs}` : '', method: 'GET' }
      },
      transformResponse: (response: ILCHttpResponse<IPaginatedNotifications>) => response,
      providesTags: ['Notifications'],
    }),
    getUnreadCount: builder.query<ILCHttpResponse<IUnreadCountByFolder>, void>({
      query: () => ({
        url: '/unread-count',
        method: 'GET',
      }),
      transformResponse: (response: ILCHttpResponse<IUnreadCountByFolder>) => response,
      providesTags: ['Notifications'],
    }),
    markAsRead: builder.mutation<ILCHttpResponse<INotification>, string>({
      query: (id) => ({
        url: `/${id}/read`,
        method: 'PUT',
      }),
      transformResponse: (response: ILCHttpResponse<INotification>) => response,
      invalidatesTags: ['Notifications'],
    }),
    markAllAsRead: builder.mutation<ILCHttpResponse<{ count: number }>, void>({
      query: () => ({
        url: '/read-all',
        method: 'PUT',
      }),
      transformResponse: (response: ILCHttpResponse<{ count: number }>) => response,
      invalidatesTags: ['Notifications'],
    }),
    markAsUnread: builder.mutation<ILCHttpResponse<INotification>, string>({
      query: (id) => ({
        url: `/${id}/unread`,
        method: 'PUT',
      }),
      transformResponse: (response: ILCHttpResponse<INotification>) => response,
      invalidatesTags: ['Notifications'],
    }),
    bulkMarkAsRead: builder.mutation<ILCHttpResponse<{ count: number }>, string[]>({
      query: (ids) => ({ url: '/bulk/read', method: 'PUT', body: { ids } }),
      transformResponse: (response: ILCHttpResponse<{ count: number }>) => response,
      invalidatesTags: ['Notifications'],
    }),
    bulkMarkAsUnread: builder.mutation<ILCHttpResponse<{ count: number }>, string[]>({
      query: (ids) => ({ url: '/bulk/unread', method: 'PUT', body: { ids } }),
      transformResponse: (response: ILCHttpResponse<{ count: number }>) => response,
      invalidatesTags: ['Notifications'],
    }),
    deleteNotification: builder.mutation<ILCHttpResponse<void>, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ILCHttpResponse<void>) => response,
      invalidatesTags: ['Notifications'],
    }),
    sendNotification: builder.mutation<ILCHttpResponse<any>, ISendNotificationParams>({
      query: (data) => ({
        url: '/messages',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ILCHttpResponse<any>) => response,
      invalidatesTags: ['Notifications'],
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useMarkAsUnreadMutation,
  useBulkMarkAsReadMutation,
  useBulkMarkAsUnreadMutation,
  useDeleteNotificationMutation,
  useSendNotificationMutation,
} = notificationsApi
